import React, { useCallback, useEffect, useState } from "react";
import PaginationControls from "../../../businessFoundation/axiom/module/js/components/PaginationControls";
import { Typography } from "../../../businessFoundation/axiom/module/js/components/Typography/typography";
import Button from "../../../businessFoundation/axiom/module/js/components/Button";
import Sheet from "../../../businessFoundation/axiom/module/js/components/Sheet";
import axios from "axios";
import { Column, MetaClassDataModel as Record, Property, SearchParams, SortType } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";
import { DataTable, DataTableBody, DataTableCell, DataTableColumnHeaderCell, DataTableContent, DataTableHeaderRow, DataTableRow, DataTableToolbar, Search } from "@episerver/ui-framework";
import { debounce } from "lodash-es";

interface ReferenceDataDialogProps {
    className: string;
    object: Record | null;
    setObject: (record: Record | null) => void;
    closeDialog: () => void
}

const ReferenceDataDialog: React.FC<ReferenceDataDialogProps> = (props) => {
    const { className, object, setObject, closeDialog } = props
    const [columns, setColumns] = useState<Column[]>([]);
    const [data, setData] = useState<Record[]>([]);
    const [selectedObject, setSelectedObject] = useState<Record | null>(object);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [searchParams, setSearchParams] = useState<SearchParams>({
        className: className,
        pageIndex: 1,
        pageSize: 10,
        keyword: "",
        sortField: "",
        sortType: SortType.Asc,
    });
    const url = window && "https://" +  window.location.host + "/EPiServer/Commerce/";
    const fetchColumns = () => {
        if (className) {
            axios
                .get(url + `BusinessFoundationData/GetBusinessFoundationImportantColumns?className=${className}`)
                .then((result) => {
                    setColumns(result.data);
                });
        }
    };

    const search = () => {
        axios.post(url + "BusinessFoundationData/Search", searchParams).then((result) => {
            const { totalPages, data } = result.data;
            setTotalPages(totalPages);
            setData(data);
        });
    };

    const handleSearch = useCallback(
        debounce((params: SearchParams, value: string) => setSearchParams({ ...params, keyword: value.trim() }), 1000),
        []
    );

    useEffect(() => {
        fetchColumns();
    }, [])

    useEffect(() => {
        search();
    }, [searchParams])

    return (
        <Sheet
            title="Select Object"
            hasCloseButton={true}
            onClose={closeDialog}
            footerButtonList={[
                <Button style="plain" key={0} onClick={closeDialog}>
                    Cancel
                </Button>,
                <Button style="highlight" key={1} onClick={() => {
                    setObject(selectedObject)
                    closeDialog()
                }}>
                    OK
                </Button>,
            ]}>
            <DataTable>
                <DataTableToolbar>
                    <React.Fragment>
                        <Search
                            onValueChange={(value: string) =>
                                handleSearch(searchParams, value)
                            }
                            onSearch={(value: string) =>
                                handleSearch(searchParams, value)
                            } />
                    </React.Fragment>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        {columns?.map((column: Column) => (
                            <DataTableColumnHeaderCell key={column.fieldName}>{column.friendlyName}</DataTableColumnHeaderCell>
                        ))}
                    </DataTableHeaderRow>
                    <DataTableBody>
                        <>
                            {columns &&
                                columns.length > 0 &&
                                data?.map((record: Record, rowIndex: number) => (
                                    <DataTableRow
                                        rowId={rowIndex.toString()}
                                        key={`row-${rowIndex}`}
                                        className={selectedObject && selectedObject.primaryKeyId == record.primaryKeyId ? "highlight-row" : ""}
                                        onClick={() => {
                                            if (!selectedObject) {
                                                setSelectedObject(record)
                                            } else {
                                                setSelectedObject(null)
                                            }
                                        }}
                                    >
                                        {columns?.map((column: Column) => (
                                            <DataTableCell key={column.fieldName}>
                                                <Typography type="body">{record.properties.find(p => p.name === column.fieldName)?.value}</Typography>
                                            </DataTableCell>
                                        ))}
                                    </DataTableRow>
                                ))}
                        </>
                    </DataTableBody>
                </DataTableContent>
            </DataTable>
            {totalPages > 1 && (
                <PaginationControls
                    currentPage={searchParams.pageIndex}
                    totalPages={totalPages}
                    goToPage={(page: number) => setSearchParams({ ...searchParams, pageIndex: page })}
                />
            )}
        </Sheet>
    );
};

export default ReferenceDataDialog;
